import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CropperJSWrapper from 'cropperjs-react';
// import CropperJSWrapper from '@components/common/cropper/wrapper'; // local version

import renderModal from '@components/show_page/modals/renderModal';
import { get as getAsset } from '@api/v4/assets';
import { get as getAttachment } from '@api/v4/attachments';
import { get as getPresets } from '@api/v4/resources/conversion_presets';
import CropDimensions from '@components/advanced_download/crop_dimensions';
import Options from '@components/advanced_download/options/main';
import Header from '@components/advanced_download/header';
import CropperFooter from '@components/advanced_download/cropper_footer';
import { track as trackLogRocket } from '@helpers/logRocket';
import {
  defaultPresets,
  defaultCroppedData,
  processExtensions,
  processImageDimensions,
  templateCroppingPreset,
} from './helper';

import '@components/advanced_download/styles/main.scss';

const AdvancedDownloadModal = ({
  attachmentKey,
  closeModal,
  editable,
  goBackToAsset,
  handlePrintuiImageSubmission,
  imageDetails,
  lockedPreset,
  resetShowModal,
  resource,
}) => {
  const [asset, setAsset] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [image, setImage] = useState();

  // default of 1 is the case where the displayed and cropped image are the same (vs. displaying a thumbnail and cropping a larger image)
  const [imageToThumbnailRatio, setImageToThumbnailRatio] = useState(1);

  const [availablePresets, setAvailablePresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [containerData, setContainerData] = useState(null);
  const [canvasData, setCanvasData] = useState(null);
  const [croppedData, setCroppedData] = useState(defaultCroppedData);
  const [zoomRatio, setZoomRatio] = useState(1);

  const [getter, setGetter] = useState({ action: '', func: () => {} });
  const [setter, setSetter] = useState({ action: '', data: {} });
  const [aspectRatio, setAspectRatio] = useState(null);
  const [fixedDimensions, setFixedDimensions] = useState(false);
  const [filetypes, setFiletypes] = useState(['']);
  const [selectedFiletype, setSelectedFiletype] = useState('');
  const [restrictOptionsType, setRestrictOptionsType] = useState(null);

  const setupLockedPreset = () => {
    if (lockedPreset?.name === "Template Cropping") {
      const templatePreset = templateCroppingPreset(lockedPreset);
      setAvailablePresets([templatePreset]);
      setSelectedPreset({ label: templatePreset.name, value: templatePreset });
      setRestrictOptionsType(['custom']);
      setAspectRatio(templatePreset.aspectRatio);
    }
  };

  const setupImageUrl = async () => {
    if (attachmentKey) {
      const attachmentOptions = { params: { fields: 'view_thumbnail_retina,updated_at,extension,conversion_options' } };
      const response = await getAttachment(attachmentKey, attachmentOptions);

      setAttachment(response);
      const { url, view_thumbnail_retina } = response.attributes;
      const imageUrl = view_thumbnail_retina || url || '';
      setImage({ url: imageUrl });
    }

    if (imageDetails?.url) {
      setImage({ url: imageDetails.url });
    }
  };

  const fetchAsset = () => {
    if (goBackToAsset) {
      const assetOptions = { params: { include: 'section' } };
      getAsset(goBackToAsset, assetOptions).then(setAsset);
    }
  };

  const fetchPresets = () => {
    if (resource) {
      const { type: resourceType, key: resourceKey } = resource;
      getPresets({ resourceType, resourceKey })
        .then((response) => {
          if (response?.length) {
            setAvailablePresets([...response, ...defaultPresets]);
          } else {
            setAvailablePresets([...defaultPresets]);
          }
        })
        .catch((err) => {
          console.log(err);
          setAvailablePresets([...defaultPresets]);
        });
    }
  };

  const setupImage = () => {
    if (attachment) {
      const { fullSizeHeight, fullSizeWidth } = processImageDimensions(attachment, canvasData);
      const {
        conversion_options,
        extension,
        filename,
        size,
        updated_at
      } = attachment.attributes;

      setImage((prevState) => ({
        ...prevState,
        extension,
        height: fullSizeHeight,
        filename,
        size,
        updated_at,
        width: fullSizeWidth,
      }));

      // update imageToThumbnailRatio if a thumbnail is displayed instead of full-sized image
      if (fullSizeWidth !== canvasData.naturalWidth) {
        setImageToThumbnailRatio(fullSizeWidth / canvasData.naturalWidth);
      }

      const uniqueConversionOptions = processExtensions(extension, conversion_options);
      setFiletypes(uniqueConversionOptions);
      setSelectedFiletype(uniqueConversionOptions[uniqueConversionOptions.length - 1]); // original filetype is last in uniqueConversionOptions
    }

    if (imageDetails && !attachment) { // <-- prioritize attachment
      const extension = processExtensions(imageDetails.mimetype);
      setImage({
        ...imageDetails,
        extension,
        height: canvasData.height,
        width: canvasData.width,
      });
      setFiletypes(extension);
      setSelectedFiletype(extension[0]);
    }
  };

  useEffect(() => {
    trackLogRocket('cropping');
    setupLockedPreset();
    setupImageUrl();
    fetchAsset(); // get assets if 'goBackToAsset' prop is present, allows for "Back" button vs. just "Close"
    fetchPresets(); // gets presets is 'resource' prop is present
  }, []);

  useEffect(() => {
    if (canvasData) {
      setZoomRatio(canvasData.width / canvasData.naturalWidth);
    }

    // update image only once, after canvasData has loaded
    if (canvasData && !image.height) {
      setupImage();
    }
  }, [canvasData]);

  useEffect(() => {
    setSetter({ action: 'setAspectRatio', data: { aspectRatio } });
  }, [aspectRatio]);

  const goBack = () => {
    if (goBackToAsset) {
      const sectionKey = asset.relationships.section.data.id;
      const [brandfolder, collection] = [BF.fx.brandfolder(), BF.fx.collection()];
      const urlBaseString = `${brandfolder.slug}/sections/${sectionKey}/ui/${asset.type}/${asset.id}`;
      let urlParamsString = collection.slug ? `?collection_slug=${collection.slug}` : "";
      if (editable) {
        const editString = 'edit=true';
        urlParamsString += urlParamsString.length > 0 ? `&${editString}` : `?${editString}`;
      }
      const url = urlBaseString + urlParamsString;

      resetShowModal();
      BF.fullModal = true;
      BF.dialog.render(encodeURIComponent(asset.attributes.name), url, BF.handlerGroups.showAsset);
    } else if (lockedPreset?.name === "Template Cropping") {
      closeModal();
    } else {
      closeModal();
    }
  };

  const updateCanvasData = (newCanvasData) => {
    // only update if canvas moves
    if (newCanvasData?.left !== canvasData?.left || newCanvasData?.top !== canvasData?.top) {
      setCanvasData(newCanvasData);
    }
  };

  const initializeCropper = () => {
    setGetter({ action: 'getCanvasData', func: setCanvasData });
    setGetter({ action: 'getContainerData', func: setContainerData });
    setGetter({ action: 'getData', func: setCroppedData });
  };

  const onZoom = () => {
    setGetter({ action: 'getCanvasData', func: setCanvasData });
  };

  const onCrop = (cropEvent) => {
    setGetter({ action: 'getCanvasData', func: updateCanvasData });
    setCroppedData(cropEvent.detail);
  };

  return (
    <section id="advanced-download-component" className="advanced-download-component">
      <Header
        closeModal={closeModal}
        goBack={goBack}
        goBackToAsset={goBackToAsset}
        image={image}
      />
      <div className="cropper-container">
        <CropperJSWrapper
          aspectRatio={lockedPreset?.aspectRatio ? lockedPreset.aspectRatio : NaN}
          imageUrl={image?.url || ''}
          onCrop={onCrop}
          onReady={initializeCropper}
          onZoom={onZoom}
          getter={getter}
          setter={setter}
          viewMode={0}
        />
        <CropDimensions
          canvasData={canvasData}
          containerData={containerData}
          croppedData={croppedData}
          fixedDimensions={fixedDimensions}
          imageToThumbnailRatio={imageToThumbnailRatio}
          selectedPreset={selectedPreset}
          zoomRatio={zoomRatio}
        />
        <CropperFooter
          imageToThumbnailRatio={imageToThumbnailRatio}
          setSetter={setSetter}
          zoomRatio={zoomRatio}
        />
      </div>
      <Options
        aspectRatio={aspectRatio}
        attachmentKey={attachmentKey}
        availablePresets={availablePresets}
        croppedData={croppedData}
        filetypes={filetypes}
        fixedDimensions={fixedDimensions}
        handlePrintuiImageSubmission={handlePrintuiImageSubmission}
        image={image}
        imageToThumbnailRatio={imageToThumbnailRatio}
        restrictOptionsType={restrictOptionsType}
        selectedFiletype={selectedFiletype}
        selectedPreset={selectedPreset}
        setAspectRatio={setAspectRatio}
        setFixedDimensions={setFixedDimensions}
        setGetter={setGetter}
        setSetter={setSetter}
        setSelectedFiletype={setSelectedFiletype}
        setSelectedPreset={setSelectedPreset}
        zoomRatio={zoomRatio}
      />
    </section>
  );
};

AdvancedDownloadModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  goBackToAsset: PropTypes.string,
  resource: PropTypes.shape({
    type: PropTypes.string,
    key: PropTypes.string,
  }),
  resetShowModal: PropTypes.func.isRequired,
  lockedPreset: PropTypes.shape({
    name: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    aspect_ratio: PropTypes.number
  }),
  handlePrintuiImageSubmission: PropTypes.func,
  // imageDetails: {
  //   mimetype: PropTypes.string,
  //   size: PropTypes.number,
  //   url: PropTypes.string,
  // },

  // one of these props must be provided, if both are provided the attachmentKey is used
  attachmentKey: (props, propName, componentName) => {
    if (!props.attachmentKey && !props.imageDetails) {
      return new Error(`One of props 'attachmentKey' or 'imageDetails' was not specified in '${componentName}'.`);
    }

    if (!PropTypes.string) {
      return new Error('invalid attachmentKey prop');
    }

    return null;
  },
  imageDetails: (props, propName, componentName) => {
    if (!props.imageDetails && !props.attachmentKey) {
      return new Error(`One of props 'imageDetails' or 'attachmentKey' was not specified in '${componentName}'.`);
    }

    return null;
  }
};

AdvancedDownloadModal.defaultProps = {
  attachmentKey: null,
  editable: false,
  goBackToAsset: null,
  lockedPreset: null,
  handlePrintuiImageSubmission: null,
  imageDetails: null,
  resource: null,
};

const ModalComponent = renderModal(AdvancedDownloadModal, 'advanced-download-modal');
export default ModalComponent;
